import { useTranslation } from 'react-i18next';
import { formatDateTimeLocaleShort, formatIngestLag, formatTime } from '@/lib/utils';

interface RecordTimestampsProps {
  /**
   * Канал записи — `call | chat | email` (`records_metadata.type`).
   *
   * Обязателен, потому что подпись первой отметки называет РАЗГОВОР, а не
   * время: у переписки и письма «Звонок 12:21» — прямая неправда, и никакой
   * вызывающий не обязан был бы про это помнить, будь поле необязательным.
   */
  type: string;
  /** Время разговора по данным источника (CRM `START_TIME`). */
  eventTime?: string | null;
  /** Время, когда Aelo принял запись. */
  createdAt?: string | null;
  /**
   * Длительность разговора в секундах — чтобы задержка считалась от его КОНЦА.
   *
   * Без неё в задержку попадала вся длительность звонка: `eventTime` это
   * `START_TIME` источника, и получасовой разговор, доставленный мгновенно,
   * подписывался как «+30м» задержки доставки.
   */
  durationSeconds?: number | null;
  className?: string;
}

/**
 * Две отметки времени записи — подписанные и обе сразу.
 *
 * Раньше список показывал одну (время звонка), карточка — другую (время
 * приёма), и ни одна не была подписана. Пользователь, у которого звонок был в
 * 12:21, а Aelo принял его в 12:46, видел «12:21» рядом со статусом
 * «анализируется» и читал это как двадцать восемь минут нашего анализа — при
 * том что пайплайн отработал за три минуты (#359).
 *
 * Обе отметки показываются всегда, даже когда расхождение мало: человек ищет
 * тот же разговор в своей системе по времени, и одно число без подписи не даёт
 * понять, почему оно не сходится с CRM.
 *
 * Разница подписывается отдельно и только когда осмысленна — правило и замеры
 * в `formatIngestLag`. Она принадлежит источнику, а не нам: приём держится на
 * backfill-кроне, потому что часть вебхуков портала до нас не доходит (#357).
 */
export function RecordTimestamps({
  type,
  eventTime,
  createdAt,
  durationSeconds,
  className,
}: RecordTimestampsProps) {
  const { t } = useTranslation('records');

  // Подпись по каналу. `call` — и умолчание для всего, чего продукт ещё не
  // знает: три значения — это весь `records_metadata.type`, а новый канал
  // придёт вместе со своим атомом.
  const eventLabel =
    type === 'chat'
      ? t('records.timestamps.chat', 'Chat')
      : type === 'email'
        ? t('records.timestamps.email', 'Email')
        : t('records.timestamps.call', 'Call');

  // Нечего показывать вовсе — пусть вызывающий не рисует разделитель.
  if (!eventTime && !createdAt) return null;

  const lag = formatIngestLag(eventTime, createdAt, durationSeconds);

  // Разбор дат — один раз на отметку, дальше передаём готовые `Date`.
  //
  // Форматтеры принимают и строку, но тогда каждый разбирает её заново: на
  // одну подпись выходило шесть разборов одних и тех же двух значений (сравнение
  // дня, две подписи, задержка). Компонент рисуется дважды на один открытый
  // детейл-вид, и цена растёт с числом мест вызова.
  const eventDate = eventTime ? new Date(eventTime) : null;
  const createdDate = createdAt ? new Date(createdAt) : null;

  // Источник не прислал времени разговора (записи из загрузки файлом, часть
  // обращений публичного API). Тогда единственная отметка — приём, и подписать
  // её всё равно нужно: иначе она снова читается как время звонка.
  if (!eventDate) {
    return (
      <span className={className}>
        {t('records.timestamps.received', 'Received')}{' '}
        {formatDateTimeLocaleShort(createdDate)}
      </span>
    );
  }

  // Дата у второй отметки — только если день другой.
  //
  // 98,5% записей приходят в тот же день (замер по проду), и повтор даты растил
  // строку с 15 символов до 52 — в узкой панели она переносилась на вторую
  // строку и выталкивала содержимое вниз. Когда день один, сравнивать надо
  // время, а дата в нём — шум.
  const sameDay = createdDate && eventDate.toDateString() === createdDate.toDateString();

  return (
    <span className={className}>
      {eventLabel} {formatDateTimeLocaleShort(eventDate)}
      {createdDate && (
        <>
          {' · '}
          {t('records.timestamps.received', 'Received')}{' '}
          {sameDay ? formatTime(createdDate) : formatDateTimeLocaleShort(createdDate)}
          {lag && ` (+${lag})`}
        </>
      )}
    </span>
  );
}
